import { useState, useContext } from "react";

import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
  Modal,
  Tooltip,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import styles from "./styles";
import { COLORS } from "./styles";

import { IneligibleSettingsContext } from "../../../context/ineligibleSettingsContext";
import { FieldList, IIneligibleSettingsContext, UserDefinedFieldList } from "../../../interfaces/ineligibleSettingInterface";
import {
  copyARIneligibleSettings,
  copyInventoryIneligibleSettings,
  previewCopyARIneligibleSettings,
  previewCopyInventoryIneligibleSettings,
} from "../../../api/copy-ineligible-settings";
import { SelectedClientContext } from "../../../context/selectedClientContext";
import { fieldTypeList } from "../../../utility/constants";
import { getFieldList, getInvFieldList, getParentCustomersByARCollateralId, getUserDefinedFieldList } from "../../../api/ineligible-settings";
import { IClient, ICollateral } from "../../../interfaces";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function ChooseSourceStep({
  collaterals,
  sourceId,
  setSourceId,
  settings,
  loadingSettings,
  checkedIds,
  toggleChecked,
  selectAll,
  clearAll,
  conflictResolution,
  setConflictResolution,
  onCancel,
  onContinue,
}: any) {
  const { selectedCollateral } =
    useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;

  const targetLabel =
    selectedCollateral?.collateralType === "AR"
      ? selectedCollateral.arCollateralName
      : selectedCollateral?.invCollateralName;

  const checkedConflicts = settings.filter(
    (s: any) =>
      checkedIds.has(s.ineligibleSettingId) && s.existsOnTarget
  );

  const selectedCount = checkedIds.size;

  return (
    <>
      <Box sx={styles.headingContainer}>
        <Box>
          <Typography sx={styles.headingTitle}>
            Copy Ineligible Settings
          </Typography>

          <Typography sx={styles.headingSubtitle}>
            Step 1 of 2 — choose a source collateral and the settings to copy
          </Typography>
        </Box>

        <Button
          aria-label="Close"
          onClick={onCancel}
          sx={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </Box>

      <Box sx={styles.divider} />

      <Box sx={styles.body}>
        <Box sx={styles.collateralRow}>
          <Box sx={styles.collateralField}>
            <Typography sx={styles.fieldLabel}>
              Copy from
              <span style={{ color: COLORS.REQUIRED }}> *</span>
            </Typography>

            <Autocomplete
              fullWidth
              options={collaterals.filter(
                (collateral: any) =>
                  collateral.recordId !== selectedCollateral?.recordId
              )}
              value={
                collaterals.find(
                  (c: any) => c.recordId === sourceId
                ) ?? null
              }
              onChange={(_, value) =>
                setSourceId(value?.recordId ?? null)
              }
              getOptionLabel={(option) =>
                option.arCollateralName ||
                option.invCollateralName ||
                option.label ||
                ""
              }
              isOptionEqualToValue={(option, value) =>
                option.recordId === value.recordId
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select collateral"
                  size="small"
                  sx={styles.autocomplete}
                />
              )}
            />
          </Box>

          <ArrowForwardIcon sx={styles.arrowIcon} />

          <Box sx={styles.collateralField}>
            <Typography sx={styles.fieldLabel}>Copy to</Typography>

            <TextField
              fullWidth
              value={targetLabel || ""}
              disabled
              size="small"
              sx={styles.autocomplete}
            />
          </Box>
        </Box>

        {!sourceId && (
          <Typography
            sx={{
              mt: 3,
              fontSize: 13,
              color: COLORS.SECONDARY_TEXT,
            }}
          >
            Select a source collateral to continue
          </Typography>
        )}

        {sourceId && (
          <Box sx={{ mt: 2.75 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={styles.sectionTitle}>
                Settings on{" "}
                {collaterals.find(
                  (c: any) => c.recordId === sourceId
                )?.label ||
                  collaterals.find(
                    (c: any) => c.recordId === sourceId
                  )?.arCollateralName ||
                  collaterals.find(
                    (c: any) => c.recordId === sourceId
                  )?.invCollateralName}
              </Typography>

              <Box sx={{ display: "flex", gap: 1.75 }}>
                <Button
                  sx={styles.linkButton}
                  onClick={selectAll}
                >
                  Select all
                </Button>

                <Button
                  sx={styles.linkButton}
                  onClick={clearAll}
                >
                  Clear
                </Button>
              </Box>
            </Box>

            <TableContainer
              sx={{
                ...styles.tableContainer,
                mt: 1.25,
              }}
            >
              {loadingSettings ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    py: 4.5,
                  }}
                >
                  <CircularProgress size={16} />

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: COLORS.SECONDARY_TEXT,
                    }}
                  >
                    Loading settings…
                  </Typography>
                </Box>
              ) : settings.length === 0 ? (
                <Box sx={{ py: 4.5, textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: COLORS.SECONDARY_TEXT,
                    }}
                  >
                    This collateral has no ineligible settings to copy.
                  </Typography>
                </Box>
              ) : (
                <Table size="small" sx={styles.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.th,
                          width: 36,
                        }}
                      />

                      <TableCell sx={styles.th}>
                        Ineligible
                      </TableCell>

                      <TableCell sx={styles.th}>
                        Criteria
                      </TableCell>

                      <TableCell sx={styles.th}>
                        Ineligible field
                      </TableCell>

                      <TableCell sx={styles.th}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {settings.map((s: any) => {
                      const invalidCount =
                        s.invalidFieldReferences?.length || 0;

                      return (
                        <TableRow
                          key={s.ineligibleSettingId}
                        >
                          <TableCell sx={styles.td}>
                            <MuiCheckbox
                              size="small"
                              checked={checkedIds.has(
                                s.ineligibleSettingId
                              )}
                              onChange={() =>
                                toggleChecked(
                                  s.ineligibleSettingId
                                )
                              }
                              sx={{ p: 0 }}
                            />
                          </TableCell>

                          <TableCell
                            sx={{
                              ...styles.td,
                              fontWeight: 500,
                            }}
                          >
                            {s.altDescription ?? s.description}
                          </TableCell>

                          <TableCell sx={styles.td}>
                            {s.criteriaCount} rule
                            {s.criteriaCount > 1 ? "s" : ""}
                          </TableCell>

                          <TableCell sx={styles.td}>
                            {s.ineligibleField || ""}
                          </TableCell>

                          <TableCell sx={styles.td}>
                            {invalidCount > 0 ? (
                              <Box
                                component="span"
                                sx={styles.chip(
                                  "#FBEAEC",
                                  COLORS.ERROR
                                )}
                              >
                                {invalidCount} invalid field
                                {invalidCount > 1 ? "s" : ""}
                              </Box>
                            ) : s.existsOnTarget ? (
                              <Box
                                component="span"
                                sx={styles.chip(
                                  "#FFF3CD",
                                  "#8A6D00"
                                )}
                              >
                                Exists on target
                              </Box>
                            ) : (
                              <Box
                                component="span"
                                sx={styles.chip(
                                  "#E6F4EA",
                                  COLORS.SUCCESS
                                )}
                              >
                                Valid
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            {checkedConflicts.length > 0 && (
              <Box sx={styles.warningBox}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: COLORS.PRIMARY_DARK,
                  }}
                >
                  {checkedConflicts.length} setting
                  {checkedConflicts.length > 1 ? "s" : ""} already exist
                  {checkedConflicts.length === 1 ? "s" : ""} on{" "}
                  {targetLabel}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: COLORS.SECONDARY_TEXT,
                    mt: "2px",
                  }}
                >
                  {checkedConflicts
                    .map((s: any) => s.description)
                    .join(", ")}
                </Typography>

                <FormControlLabel
                  sx={styles.radioLabel}
                  control={
                    <Radio
                      name="conflict"
                      size="small"
                      checked={conflictResolution === "REPLACE"}
                      onChange={() =>
                        setConflictResolution("REPLACE")
                      }
                    />
                  }
                  label={`Replace the existing settings on ${targetLabel}`}
                />

                <FormControlLabel
                  sx={styles.radioLabel}
                  control={
                    <Radio
                      name="conflict"
                      size="small"
                      checked={conflictResolution === "SKIP"}
                      onChange={() =>
                        setConflictResolution("SKIP")
                      }
                    />
                  }
                  label="Keep existing settings and skip the conflicting ones"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={styles.divider} />

      <Box sx={styles.footer}>
        <Typography sx={styles.footerNote}>
          {sourceId
            ? `${selectedCount} setting${
                selectedCount === 1 ? "" : "s"
              } selected`
            : ""}
        </Typography>

        <Box sx={styles.buttonRow}>
          <Button
            sx={styles.cancelButton}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            sx={styles.primaryButton(selectedCount === 0)}
            disabled={selectedCount === 0}
            onClick={onContinue}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </>
  );
}

function ValidationStep({
  sourceLabel,
  targetLabel,
  selectedSettings,
  targetFields,
  conflictResolution,
  resolutions,
  setResolution,
  removeRule,
  onBack,
  onCancel,
  onConfirm,
  submitting,
}: any) {
  const invalidRows = selectedSettings.flatMap((s: any) =>
    (s.invalidFieldReferences || [])
      .filter((ref: any) => {
        const key =
          ref.sourceIneligibleSettingId ??
          ref.sourceIneligibleRuleConditionId ??
          ref.sourceIneligibleRuleId;

        return resolutions[key]?.type !== "removed";
      })
      .map((ref: any) => {
        const key =
          ref.sourceIneligibleSettingId ??
          ref.sourceIneligibleRuleConditionId ??
          ref.sourceIneligibleRuleId;

        return {
          ...ref,
          resolutionId: key,
          settingId: s.ineligibleSettingId,
          settingName: s.altDescription ?? s.description,
        };
      })
  );

  const unresolvedCount = invalidRows.filter(
    (r: any) =>
      !resolutions[r.resolutionId] ||
      resolutions[r.resolutionId].type === "unmapped"
  ).length;

  // NOTE: row.fieldName is the raw field key from the backend
  // (CopyFieldReferenceDTO.fieldName, e.g. "custSrcId") - the same thing
  // targetFields stores as `.field`, not `.description` (the friendly
  // label). Comparing against `.description` here meant sourceField was
  // always undefined, so this always returned [] before reaching the
  // Ineligible Field / numeric-user-defined filtering below.
  const getTargetFieldOptions = (row: any) => {
    const sourceField = targetFields.find(
      (field) => field.field === row.fieldName
    );

    if (!sourceField) {
      return [];
    }

    if (row.location === 'Ineligible Field') {
      return targetFields
      .filter(
        (field) =>
          field.fieldType === 'number' &&
          field.isUserDefined &&
          field.field !== sourceField.field
      );
    }

    return targetFields
      .filter(
        (field) =>
          field.fieldType === sourceField.fieldType &&
          field.field !== sourceField.field
      );
  };

  return (
    <>
      <Box sx={styles.headingContainer}>
        <Box>
          <Typography sx={styles.headingTitle}>
            Validate copied settings
          </Typography>

          <Typography sx={styles.headingSubtitle}>
            Step 2 of 2 — {sourceLabel} → {targetLabel}
          </Typography>
        </Box>

        <Button
          aria-label="Close"
          onClick={onCancel}
          sx={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </Box>

      <Box sx={styles.divider} />

      <Box sx={styles.body}>
        {invalidRows.length > 0 && (
          <Box sx={styles.errorBox}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: COLORS.ERROR_DARK,
              }}
            >
              {invalidRows.length} field
              {invalidRows.length > 1 ? "s" : ""} cannot be copied
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: COLORS.ERROR_DARK,
                mt: "2px",
              }}
            >
              These fields do not exist on {targetLabel}. Map each one
              to a field on the target or remove the rule before
              copying. Invalid rules are not saved.
            </Typography>
          </Box>
        )}

        {invalidRows.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                ...styles.sectionTitle,
                mb: 1,
              }}
            >
              Invalid fields
            </Typography>

            <TableContainer sx={styles.tableContainer}>
              <Table size="small" sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.th}>
                      Ineligible
                    </TableCell>

                    <TableCell sx={styles.th}>
                      Field name
                    </TableCell>

                    <TableCell sx={styles.th}>
                      Issue
                    </TableCell>

                    <TableCell sx={styles.th}>
                      Resolution
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {invalidRows.map((row: any) => {
                    const resolution =
                      resolutions[row.resolutionId] || {
                        type: "unmapped",
                      };

                    const isUnresolved =
                      resolution.type === "unmapped";

                    return (
                      <TableRow
                        key={row.resolutionId}
                        sx={{
                          bgcolor: isUnresolved
                            ? "#FDF2F3"
                            : "transparent",
                        }}
                      >
                        <TableCell sx={styles.td}>
                          {row.settingName}
                        </TableCell>

                        <TableCell sx={styles.td}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: COLORS.ERROR,
                              fontSize: 13,
                            }}
                          >
                            {fieldTypeList.find(field => field.fieldName === row.fieldName)?.field ?? row.fieldName}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 11,
                              color: COLORS.ERROR_LIGHT,
                            }}
                          >
                            {row.location}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            ...styles.td,
                            color: COLORS.SECONDARY_TEXT,
                          }}
                        >
                          Field does not exist on the target
                          collateral
                        </TableCell>

                        <TableCell sx={styles.td}>
                          <Box sx={styles.resolutionRow}>
                            <Autocomplete
                              options={getTargetFieldOptions(row)}
                              value={
                                resolution.type === "mapped"
                                  ? targetFields.find(
                                      (field) => field.field === resolution.field
                                    ) ?? null
                                  : null
                              }
                              onChange={(_, value) => {
                                if (value) {
                                  setResolution(row.resolutionId, {
                                    type: "mapped",
                                    field: value.field,
                                  });
                                }
                              }}
                              getOptionLabel={(option) =>
                                option.description || option.field
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.field === value.field
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option.field}>
                                  <Box>
                                    <Typography sx={{ fontSize: 13 }}>
                                      {option.description}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Map to a field on target…"
                                  size="small"
                                  sx={
                                    resolution.type === "mapped"
                                      ? styles.resolutionAutocompleteMapped
                                      : styles.resolutionAutocomplete
                                  }
                                />
                              )}
                            />

                            <Button
                              variant="outlined"
                              onClick={() =>
                                removeRule(
                                  row.resolutionId
                                )
                              }
                              sx={styles.removeRuleButton}
                            >
                              Remove rule
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {unresolvedCount > 0 && (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: 13,
                  color: COLORS.SECONDARY_TEXT,
                }}
              >
                {unresolvedCount} item
                {unresolvedCount > 1 ? "s" : ""} still need
                {unresolvedCount === 1 ? "s" : ""} a decision.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2.5 }}>
          <Typography
            sx={{
              ...styles.sectionTitle,
              mb: 1,
            }}
          >
            Will be copied
          </Typography>

          <Box
            sx={{
              border: `1px solid ${COLORS.NEUTRAL}`,
              borderRadius: "4px",
            }}
          >
            {selectedSettings.map((s: any, idx: number) => {
              const skippedForConflict =
                s.existsOnTarget &&
                conflictResolution === "SKIP";

              const settingInvalidRefs =
                s.invalidFieldReferences || [];

              const unresolvedRefs =
                settingInvalidRefs.filter((ref: any) => {
                  const key =
                    ref.sourceIneligibleSettingId ??
                    ref.sourceIneligibleRuleConditionId ??
                    ref.sourceIneligibleRuleId;

                  const resolution = resolutions[key];

                  return (
                    !resolution ||
                    resolution.type === "unmapped"
                  );
                });

              const hasUnresolved =
                unresolvedRefs.length > 0;

              let icon;
              let text;
              let textColor = COLORS.SECONDARY_TEXT;

              if (skippedForConflict) {
                icon = (
                  <CancelIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.DISABLED,
                    }}
                  />
                );

                text =
                  "skipped — conflicts with existing setting";
              } else if (hasUnresolved) {
                icon = (
                  <CancelIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.ERROR,
                    }}
                  />
                );

                const n = unresolvedRefs.length;

                text = `${n} unresolved field reference${
                  n > 1 ? "s" : ""
                }`;

                textColor = COLORS.ERROR;
              } else {
                icon = (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.SUCCESS,
                    }}
                  />
                );

                text = s.existsOnTarget
                  ? `replaces existing setting · ${s.criteriaCount} rule${
                      s.criteriaCount > 1 ? "s" : ""
                    }`
                  : `${s.criteriaCount} rule${
                      s.criteriaCount > 1 ? "s" : ""
                    }`;
              }

              return (
                <Box
                  key={s.ineligibleSettingId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "9px 14px",
                    borderTop:
                      idx === 0
                        ? "none"
                        : `1px solid ${COLORS.NEUTRAL_LIGHT}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {icon}

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: skippedForConflict
                          ? COLORS.SECONDARY_TEXT
                          : COLORS.PRIMARY_TEXT,
                        textDecoration: skippedForConflict
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {s.altDescription ?? s.description}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: textColor,
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.divider} />

      <Box sx={styles.footer}>
        <Typography sx={styles.footerNote}>
          {unresolvedCount > 0
            ? "Copy is blocked until every invalid field is mapped or removed"
            : ""}
        </Typography>

        <Box sx={styles.buttonRow}>
          <Button
            sx={styles.cancelButton}
            onClick={onBack}
          >
            Back
          </Button>

          <Button
            sx={styles.cancelButton}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            sx={styles.primaryButton(
              unresolvedCount > 0 || submitting
            )}
            disabled={
              unresolvedCount > 0 || submitting
            }
            onClick={onConfirm}
            startIcon={
              submitting ? (
                <CircularProgress
                  size={14}
                  color="inherit"
                />
              ) : undefined
            }
          >
            Copy settings
          </Button>
        </Box>
      </Box>
    </>
  );
}

interface TargetFieldOption {
  field: string;
  description: string;
  fieldType?: string;
  isUserDefined: boolean;
}

function CopyIneligibleModal({
  onClose,
  onCopied,
  collaterals,
}: any) {
  const { selectedClient } = useContext(SelectedClientContext);
  const {
    selectedCollateral,
    fieldList,
    userDefinedFieldList,
    setToaster,
  } = useContext(
    IneligibleSettingsContext
  ) as IIneligibleSettingsContext;

  const [step, setStep] = useState(1);
  const [sourceId, setSourceId] =
    useState<string | null>(null);
  const [settings, setSettings] = useState<any[]>([]);
  const [loadingSettings, setLoadingSettings] =
    useState(false);
  const [checkedIds, setCheckedIds] =
    useState<Set<number>>(new Set());
  const [conflictResolution, setConflictResolution] =
    useState<"REPLACE" | "SKIP">("REPLACE");
  const [resolutions, setResolutions] =
    useState<Record<string, any>>({});
  const [submitting, setSubmitting] =
    useState(false);

  const targetFields: TargetFieldOption[] = [
    ...(Array.isArray(fieldList)
      ? fieldList
        .map((field: FieldList) => {
          const fieldType = fieldTypeList.find(f => f.field === field.description);
          return {
            field: field.tableField,
            description: field.description,
            fieldType: fieldType?.fieldType ?? field.fieldType,
            isUserDefined: field.isUserDefined,
          };
        })
      : [])
  ];

  async function handleSetSourceId(
    id: string | null
  ) {
    setSourceId(id);
    setSettings([]);
    setCheckedIds(new Set());
    setResolutions({});
    setStep(1);

    if (!id || !selectedClient || !selectedCollateral) {
      return;
    }

    setLoadingSettings(true);

    try {
      const response =
        selectedCollateral.collateralType == 'AR'
          ? await previewCopyARIneligibleSettings(
            selectedClient.recordId as number,
            Number(id),
            selectedCollateral.recordId
          )
          : await previewCopyInventoryIneligibleSettings(
            selectedClient.recordId as number,
            Number(id),
            selectedCollateral.recordId
          );;

      setSettings(response.settings.sort((a, b) => a.order - b.order) || []);
    } catch (error) {
      setToaster({
        isOpen: true,
        message:
          "Unable to load ineligible settings for the selected collateral.",
        severity: "error",
      });
    } finally {
      setLoadingSettings(false);
    }
  }

  function toggleChecked(id: number) {
    setCheckedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  const selectedSettings = settings.filter((s) =>
    checkedIds.has(s.ineligibleSettingId)
  );

  const hasInvalidFields = selectedSettings.some(
    (s) =>
      (s.invalidFieldReferences?.length || 0) > 0
  );

  function handleContinue() {
    if (hasInvalidFields) {
      setStep(2);
      return;
    }

    handleConfirm();
  }

  function setResolution(
    refId: string | number,
    resolution: any
  ) {
    setResolutions((prev) => ({
      ...prev,
      [String(refId)]: resolution,
    }));
  }

  function removeRule(refId: string | number) {
    setResolutions((prev) => ({
      ...prev,
      [String(refId)]: {
        type: "removed",
      },
    }));
  }

  function getResolutionKey(ref: any) {
    return String(
      ref.sourceIneligibleSettingId ??
        ref.sourceIneligibleRuleConditionId ??
        ref.sourceIneligibleRuleId
    );
  }

  async function handleConfirm() {
    if (
      !selectedClient ||
      !selectedCollateral ||
      !sourceId
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const selectedIneligibleSettingIds =
        selectedSettings
          .filter(
            (setting) =>
              !(
                setting.existsOnTarget &&
                conflictResolution === "SKIP"
              )
          )
          .map(
            (setting) =>
              setting.ineligibleSettingId
          );

      const fieldResolutions = selectedSettings.flatMap(
        (setting) =>
          (setting.invalidFieldReferences || [])
            .map((ref: any) => {
              const resolution =
                resolutions[getResolutionKey(ref)];

              if (!resolution) {
                return null;
              }

              if (resolution.type === "removed") {
                return {
                  action: "REMOVE",
                  sourceIneligibleRuleId:
                    ref.sourceIneligibleRuleId,
                  sourceIneligibleRuleConditionId:
                    ref.sourceIneligibleRuleConditionId,
                };
              }

              if (resolution.type === "mapped") {
                return {
                  action: "MAP",
                  sourceIneligibleRuleId:
                    ref.sourceIneligibleRuleId,
                  sourceIneligibleRuleConditionId:
                    ref.sourceIneligibleRuleConditionId,
                  mappedField: resolution.field,
                };
              }

              return null;
            })
            .filter(Boolean)
      );

      // BUG FIX: this always called the AR copy function before, even when
      // the target collateral is Inventory. Preview already branched on
      // collateralType - commit needs to match it.
      const result =
        selectedCollateral.collateralType === "AR"
          ? await copyARIneligibleSettings(
              selectedClient.recordId as number,
              Number(sourceId),
              selectedCollateral.recordId,
              selectedIneligibleSettingIds,
              conflictResolution,
              fieldResolutions
            )
          : await copyInventoryIneligibleSettings(
              selectedClient.recordId as number,
              Number(sourceId),
              selectedCollateral.recordId,
              selectedIneligibleSettingIds,
              conflictResolution,
              fieldResolutions
            );

      setToaster({
        isOpen: true,
        message: `${result.copiedCount} ineligible setting${
          result.copiedCount === 1 ? "" : "s"
        } copied successfully.`,
        severity: "success",
      });

      onCopied(result);
    } catch (error) {
      setToaster({
        isOpen: true,
        message:
          "Unable to copy ineligible settings.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const sourceCollateral = collaterals.find(
    (c: any) => c.recordId === sourceId
  );

  const sourceLabel =
    sourceCollateral?.label ||
    sourceCollateral?.arCollateralName ||
    sourceCollateral?.invCollateralName ||
    "";

  const targetLabel =
    selectedCollateral?.collateralType === "AR"
      ? selectedCollateral?.arCollateralName
      : selectedCollateral?.invCollateralName;

  return (
    <Modal
      open
      onClose={onClose}
      aria-labelledby="copy-ineligible-modal-title"
    >
      <Box sx={styles.modalBackground}>
        {step === 1 ? (
          <ChooseSourceStep
            collaterals={collaterals}
            sourceId={sourceId}
            setSourceId={handleSetSourceId}
            settings={settings}
            loadingSettings={loadingSettings}
            checkedIds={checkedIds}
            toggleChecked={toggleChecked}
            selectAll={() =>
              setCheckedIds(
                new Set(
                  settings.map(
                    (s) => s.ineligibleSettingId
                  )
                )
              )
            }
            clearAll={() =>
              setCheckedIds(new Set())
            }
            conflictResolution={conflictResolution}
            setConflictResolution={
              setConflictResolution
            }
            onCancel={onClose}
            onContinue={handleContinue}
          />
        ) : (
          <ValidationStep
            sourceLabel={sourceLabel}
            targetLabel={targetLabel}
            selectedSettings={selectedSettings}
            targetFields={targetFields}
            conflictResolution={conflictResolution}
            resolutions={resolutions}
            setResolution={setResolution}
            removeRule={removeRule}
            onBack={() => setStep(1)}
            onCancel={onClose}
            onConfirm={handleConfirm}
            submitting={submitting}
          />
        )}
      </Box>
    </Modal>
  );
}

export default function CopyFrom({
  collaterals,
  onReload
}: any) {
  const { selectedClient } = useContext(SelectedClientContext);
  const { selectedCollateral, isIneligibleDetailsLoading, isFormikDirty, setFieldList, setUserDefinedFieldList } =
    useContext(
      IneligibleSettingsContext
    ) as IIneligibleSettingsContext;

  const [modalOpen, setModalOpen] = useState(false);

  function handleOpen() {
    if (isFormikDirty) {
      return;
    }

    setModalOpen(true);
    loadFieldRelatedDependencies();
  }

  function handleCopied(_result: any) {
    onReload();
    setModalOpen(false);
  }

  const loadFieldRelatedDependencies = async () => {
    const [
      loadedFieldList,
      loadedUserDefinedFieldList
    ] = await Promise.all([
       (
          (selectedCollateral?.collateralType == 'AR' || selectedCollateral?.collateralType == undefined)
            ? getFieldList(selectedCollateral as ICollateral)
            : getInvFieldList(selectedCollateral as ICollateral)
        ),
        getUserDefinedFieldList(selectedClient as Required<IClient>, selectedCollateral as ICollateral)
      ]);

    const allFieldList = [
      ...loadedFieldList,
      ...loadedUserDefinedFieldList.map(field => ({
          documentTypeFk: field.documentTypeFk, tableField: field.fieldName,
          description: field.fieldName, fieldType: field.fieldType, isUserDefined: true,
      }))
    ].sort((a, b) => a.description.localeCompare(b.description)) as FieldList[];

    setFieldList(allFieldList);
    setUserDefinedFieldList(loadedUserDefinedFieldList);
  };

  return (
    <Box sx={{ marginLeft: 'auto' }}>
      <Tooltip title='Copy From'>
        <IconButton onClick={handleOpen} aria-label='Copy From' disabled={isIneligibleDetailsLoading || selectedCollateral === null || isFormikDirty}>
          <ContentCopyIcon sx={styles.copyIcon}/>
        </IconButton>
      </Tooltip>

      {modalOpen && !isFormikDirty && (
        <CopyIneligibleModal
          onClose={() => setModalOpen(false)}
          onCopied={handleCopied}
          collaterals={collaterals}
        />
      )}
    </Box>
  );
}